import React from 'react';
import { Link } from 'react-router-dom';

const FinancialDisclosure = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div
        className="py-14 px-6 text-white"
        style={{
          background: 'linear-gradient(135deg, #F06F6B 0%, #F07A5E 40%, #F58A54 100%)',
        }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-1">Financial Product Disclosure</h1>
          <p className="text-white/90 text-base font-medium mb-1">Stackd</p>
          <p className="text-white/80 text-sm">Last Updated: 3/12/2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">

          {/* Section 1 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">1. No Financial Advice</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Stackd ("Stackd Financial," "Stackd," "we," "our," or "us") operates the Stackd platform to help users research, organize, and compare major purchases, financial products, and services.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              The information provided on Stackd is for informational and educational purposes only. Nothing on the Platform should be considered financial, investment, legal, tax, medical, or professional advice.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Stackd does not provide personalized financial advice or recommendations. Users should consult a qualified financial advisor, accountant, attorney, or other professional before making significant financial decisions.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">2. Not a Lender or Financial Institution</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Stackd is not a bank, lender, insurance provider, broker, or financial institution.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3">Stackd does not:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 pl-2 mb-4">
              <li>Issue loans</li>
              <li>Approve or deny credit applications</li>
              <li>Set interest rates</li>
              <li>Provide insurance coverage</li>
              <li>Offer financial products directly</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Financial products and services presented on the Platform are offered by third‑party providers.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">3. Third‑Party Financial Products</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Stackd may display or link to financial products such as:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 pl-2 mb-4">
              <li>Personal loans</li>
              <li>Auto loans</li>
              <li>Credit cards</li>
              <li>Insurance products</li>
              <li>Financing options</li>
              <li>Other financial services</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              These products are provided and administered by third‑party companies. Stackd does not control the terms, pricing, availability, or eligibility requirements for these products.
            </p>
            <p className="text-gray-700 leading-relaxed">
              All decisions regarding approval, rates, fees, and final terms are made by the third‑party provider.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">4. No Guarantee of Approval or Rates</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Any estimated rates, pricing, or terms displayed on the Platform are provided for informational purposes only.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3">Stackd does not guarantee:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 pl-2 mb-4">
              <li>Loan approval</li>
              <li>Eligibility for financial products</li>
              <li>Interest rates or payment terms</li>
              <li>Availability of offers</li>
              <li>Final pricing</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Actual terms may vary based on factors including credit history, location, financial circumstances, and the policies of the provider.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">5. Affiliate Relationships</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Stackd may receive compensation when users click links, submit requests, or apply for financial products through the Platform.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              This compensation may include referral fees, affiliate commissions, advertising payments, or other forms of revenue sharing.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Compensation will not influence which financial products appear on the Platform and how they are displayed.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Sponsored vendors and positioning will be clearly defined and at user's discretion to include for analysis.
            </p>
            <p className="text-gray-700 leading-relaxed">
              For more information, please review our Affiliate Disclosure.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">6. Accuracy of Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Stackd strives to present accurate and up‑to‑date information; however, financial product terms and offers may change at any time.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Stackd does not guarantee the completeness, accuracy, or timeliness of any information provided on the Platform.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Users should verify all information directly with the financial provider before making decisions.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">7. User Responsibility</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Users are solely responsible for evaluating financial products and determining whether they are appropriate for their personal circumstances.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Stackd encourages users to conduct independent research and consult professional advisors when necessary.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">8. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              If you have questions about this Financial Disclaimer, please contact:
            </p>
            <p className="text-gray-700 leading-relaxed">
              Email:{' '}
              <a
                href="mailto:infor@saveonstackd.com"
                className="text-orange-500 hover:text-orange-600 transition-colors"
              >
                infor@saveonstackd.com
              </a>
            </p>
          </section>

          {/* Back link */}
          <div className="mt-10 pt-6 border-t border-gray-100">
            <Link
              to="/"
              className="text-sm font-medium text-orange-500 hover:text-orange-600 transition-colors"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialDisclosure;
